/*
 * EquaVetz below-cart savings banner + Add to cart price suffix.
 *
 * The pack picker itself is fully native. Dawn's product-info.js drives every
 * variant update. The only thing Dawn does not re-render is content BELOW the
 * buy button, so this element keeps the "You save $X" banner in sync by
 * subscribing to Dawn's OWN PUB_SUB_EVENTS.variantChange event (published by
 * product-info.js) and copying the freshly server-rendered value out of the
 * section HTML that Dawn already fetched. No extra network request, no price
 * math duplicated in JS. It mirrors exactly what Liquid rendered, the same way
 * Dawn's updateSourceFromDestination mirrors price/inventory.
 */

/* ------------------------------------------------ Add to cart price ------
 * The "- $199.95" suffix on the Add to cart button has to track two things
 * Dawn does not join up for us: the selected variant AND the quantity. Dawn's
 * toggleAddButton only rewrites the button's label span, so the sibling
 * .ev-atc-price span carries the variant's UNIT price in cents
 * (data-unit-price, rendered by snippets/buy-buttons.liquid) and this module
 * multiplies it by the quantity input on every change.
 *
 * The unit price is refreshed from the section HTML Dawn already fetched on
 * variantChange, so there is still no extra request and no price maths beyond
 * the multiply. Formatting goes through the shop's own money_format rather than
 * Intl, so the suffix matches every other price Liquid renders on the page.
 */

const EV_ATC_PREFIX = 'ProductSubmitButton-';
const EV_QTY_PREFIX = 'Quantity-';
const EV_FORM_PREFIX = 'product-form-';

function evFormatWithDelimiters(cents, precision, thousands, decimal) {
  if (!isFinite(cents)) return '0';
  const fixed = (cents / 100).toFixed(precision);
  const parts = fixed.split('.');
  const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
  return parts[1] ? whole + decimal + parts[1] : whole;
}

/* Mirrors Shopify's own money filter so the suffix cannot drift from the
   prices Liquid renders elsewhere on the page. */
function evFormatMoney(cents, format) {
  const template = format || '${{amount}}';
  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const match = template.match(placeholder);
  let value;

  switch (match && match[1]) {
    case 'amount_no_decimals':
      value = evFormatWithDelimiters(cents, 0, ',', '.');
      break;
    case 'amount_with_comma_separator':
      value = evFormatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = evFormatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_space_separator':
      value = evFormatWithDelimiters(cents, 2, ' ', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = evFormatWithDelimiters(cents, 0, ' ', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = evFormatWithDelimiters(cents, 2, "'", '.');
      break;
    default:
      value = evFormatWithDelimiters(cents, 2, ',', '.');
  }

  return template.replace(placeholder, value);
}

/* Quantity input, Add to cart button and product form all key off the same
   section id, so either element can find the other without a shared wrapper,
   which matters because the quick-add modal renders them in its own subtree. */
function evSectionIdFromQuantity(input) {
  const formId = input.getAttribute('form') || '';
  if (formId.startsWith(EV_FORM_PREFIX)) return formId.slice(EV_FORM_PREFIX.length);

  const button = input.closest('product-info, quick-add-modal, .product')?.querySelector('.ev-atc-price');
  const owner = button?.closest('[id^="' + EV_ATC_PREFIX + '"]');
  return owner ? owner.id.slice(EV_ATC_PREFIX.length) : null;
}

/* Dawn's ProductInfo.sectionId getter returns `originalSection || section`, so
   inside the quick-add modal every event carries the ORIGINAL section id while
   the modal's own DOM ids are prefixed with "quickadd-". Map the published id
   back to whichever live sections it actually drives. The fetched HTML always
   uses the published (unprefixed) id. */
function evLiveSectionIds(publishedId) {
  const ids = [];
  if (document.getElementById(EV_ATC_PREFIX + publishedId)) ids.push(publishedId);
  document
    .querySelectorAll(`product-info[data-original-section="${publishedId}"]`)
    .forEach((info) => {
      const live = info.dataset.section;
      if (live && !ids.includes(live)) ids.push(live);
    });
  return ids;
}

function evUpdateAtcPrice(sectionId) {
  const button = document.getElementById(EV_ATC_PREFIX + sectionId);
  const priceEl = button && button.querySelector('.ev-atc-price');
  if (!priceEl) return;

  // No data-unit-price means the variant is sold out or unavailable: Liquid
  // rendered the span empty on purpose, so leave it that way.
  const unit = parseInt(priceEl.dataset.unitPrice, 10);
  if (!Number.isFinite(unit)) {
    priceEl.textContent = '';
    return;
  }

  const qtyInput = document.getElementById(EV_QTY_PREFIX + sectionId);
  let quantity = parseInt(qtyInput ? qtyInput.value : '1', 10);
  if (!Number.isFinite(quantity) || quantity < 1) quantity = 1;

  priceEl.textContent = evFormatMoney(unit * quantity, priceEl.dataset.moneyFormat);
}

/* Delegated so it covers the PDP and the quick-add modal, whose form is
   injected long after this script runs. Dawn's quantity-input +/- buttons
   dispatch a bubbling "change" on the input, and typing fires "input". */
function evOnQuantityEvent(event) {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.name !== 'quantity') return;
  const sectionId = evSectionIdFromQuantity(input);
  if (sectionId) evUpdateAtcPrice(sectionId);
}

document.addEventListener('change', evOnQuantityEvent);
document.addEventListener('input', evOnQuantityEvent);

if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
    const data = event && event.data;
    if (!data || !data.html || !data.sectionId) return;

    // Take the new unit price from the section Dawn already fetched, then let
    // the shared updater re-apply the quantity that is on screen right now.
    const next = data.html.querySelector(`#${EV_ATC_PREFIX}${data.sectionId} .ev-atc-price`);
    const unit = next ? next.getAttribute('data-unit-price') : null;
    const format = next ? next.getAttribute('data-money-format') : null;

    evLiveSectionIds(data.sectionId).forEach((liveId) => {
      const current = document.querySelector(`#${EV_ATC_PREFIX}${liveId} .ev-atc-price`);
      if (!current) return;

      if (unit === null) {
        delete current.dataset.unitPrice;
      } else {
        current.dataset.unitPrice = unit;
        if (format !== null) current.dataset.moneyFormat = format;
      }

      evUpdateAtcPrice(liveId);
    });
  });
}

if (!customElements.get('ev-savings-mirror')) {
  customElements.define(
    'ev-savings-mirror',
    class EvSavingsMirror extends HTMLElement {
      connectedCallback() {
        // subscribe / PUB_SUB_EVENTS are Dawn globals (pubsub.js, constants.js).
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const data = event && event.data;
          if (!data || data.sectionId !== this.dataset.section) return;
          const source = data.html && data.html.getElementById(this.id);
          if (source) this.innerHTML = source.innerHTML;
        });
      }

      disconnectedCallback() {
        this.unsubscribe?.();
      }
    }
  );
}
